import { zodResolver } from '@hookform/resolvers/zod';
import { Filter, Plus, RotateCcw } from 'lucide-react';
import { forwardRef } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

import {
  ALL_CUSTOMERS,
  FiltersFormValues,
  filtersFormSchema,
  formValuesToScope,
  getCurrentMonthScope,
  scopeToFormValues,
} from '../filtersSchema';

import { ColumnFilterRow } from './ColumnFilterRow';

import { CustomerSelect } from '@/components/CustomerSelect';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { VisionMonitorScope } from '@/services/VisionMonitorService';

interface FiltersFormProps {
  scope: VisionMonitorScope;
  onApply: (scope: VisionMonitorScope) => void;
}

const EMPTY_FILTER: FiltersFormValues['filters'][number] = {
  field: 'visionStatus',
  operator: 'in',
  value: '',
  values: [],
};

export function FiltersForm({ scope, onApply }: FiltersFormProps) {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FiltersFormValues>({
    resolver: zodResolver(filtersFormSchema),
    // `values` keeps the form in sync when the page changes the scope
    // itself (reset button, metric shortcuts).
    values: scopeToFormValues(scope),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'filters',
  });

  function handleApply(values: FiltersFormValues) {
    onApply(formValuesToScope(values));
  }

  function handleReset() {
    const initial = getCurrentMonthScope();

    reset(scopeToFormValues(initial));
    onApply(initial);
  }

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <form onSubmit={handleSubmit(handleApply)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-5">
            <div className="space-y-1">
              <Label>Customer</Label>
              <Controller
                control={control}
                name="customerId"
                render={({ field }) => (
                  <CustomerSelect
                    value={field.value}
                    onValueChange={field.onChange}
                    additionalOptions={[
                      { value: ALL_CUSTOMERS, label: 'All customers' },
                    ]}
                  />
                )}
              />
            </div>

            <DateField
              id="createdFrom"
              label="Created from"
              error={errors.createdFrom?.message}
              {...register('createdFrom')}
            />
            <DateField
              id="createdTo"
              label="Created to"
              error={errors.createdTo?.message}
              {...register('createdTo')}
            />
            <DateField
              id="releaseFrom"
              label="Release from"
              error={errors.releaseFrom?.message}
              {...register('releaseFrom')}
            />
            <DateField
              id="releaseTo"
              label="Release to"
              error={errors.releaseTo?.message}
              {...register('releaseTo')}
            />
          </div>

          {fields.length > 0 && (
            <div className="space-y-3 border-t pt-4">
              {fields.map((item, index) => (
                <ColumnFilterRow
                  key={item.id}
                  index={index}
                  control={control}
                  setValue={setValue}
                  error={
                    errors.filters?.[index]?.value?.message ??
                    errors.filters?.[index]?.values?.message ??
                    errors.filters?.[index]?.field?.message
                  }
                  onRemove={() => remove(index)}
                />
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append(EMPTY_FILTER)}
              disabled={fields.length >= 20}
            >
              <Plus className="h-4 w-4" />
              Add column filter
            </Button>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleReset}
              >
                <RotateCcw className="h-4 w-4" />
                Reset to current month
              </Button>
              <Button type="submit" size="sm">
                <Filter className="h-4 w-4" />
                Apply filters
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

interface DateFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  error?: string;
}

const DateField = forwardRef<HTMLInputElement, DateFieldProps>(
  ({ id, label, error, ...props }, ref) => (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="date" ref={ref} {...props} />
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  ),
);
DateField.displayName = 'DateField';
